import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { GlobalNotifications, Intent, Wizard } from '../../../../Modules';
import { Button } from 'semantic-ui-react';
import ConfirmEventDetails from './ConfirmEventDetails';
import { commitMutation, graphql } from 'react-relay';
import nullifyEmptyStrings from '../../../../../lib/utils/nullifyEmptyStrings';
import DateUtils from '../../../../../lib/utils/date';
import EmailConfirmation from '../../../../Document/Sections/Actions/Share/EmailConfirmation';
import ShareOptions from '../../../../Document/Sections/Actions/Share/ShareOptions';

class ShareWorkspace extends Component {

  static propTypes = {
    event: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    workspace: PropTypes.object.isRequired
  }

  constructor(props) {
    super(props);
    this.onOpen = this.onOpen.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onValidSubmit = this.onValidSubmit.bind(this);
    this.onInvalidSubmit = this.onInvalidSubmit.bind(this);
    this.getUserContactEmails = this.getUserContactEmails.bind(this);
    this.toggleLink = this.toggleLink.bind(this);
    this.sendEmail = this.sendEmail.bind(this);
    this.updateEvent = this.updateEvent.bind(this);
    this.state = {
      needsEventInfo: this.needsEventInfo(),
      showLink: !this.props.workspace.can_email,
      open: false,
      to: ''
    };
  }

  onOpen() {
    this.setState({
      open: true,
      needsEventInfo: this.needsEventInfo(),
      showLink: !this.props.workspace.can_email,
      to: ''
    });
  }

  onClose() {
    this.setState({ open: false });
  }

  onValidSubmit(model, resetForm, invalidateForm, successCb) {
    if (new Date(model.end_at) < new Date(model.start_at)) {
      GlobalNotifications.show({
        message: 'Unable to update event information',
        intent: Intent.DANGER
      });
      invalidateForm({ end_at: 'Event must end later than it starts' });
    } else {
      this.updateEvent(model, successCb);
    }
  }

  onInvalidSubmit() {
    GlobalNotifications.show({
      message: 'Unable to update event information',
      intent: Intent.DANGER
    });
  }

  getUserContactEmails() {
    const contacts = [];
    const { event } = this.props;
    if (event) {
      event.user_contacts.edges.map(({ node: uc }) =>
        uc.user_contact_emails.edges.map(({ node: email }) => {
          contacts.push({
            title: `${uc.first_name} ${uc.last_name}`,
            description: email.address,
            value: email.address
          });
          return email;
        })
      );
    }
    return contacts;
  }

  toggleLink() {
    this.setState(prevState => ({ showLink: !prevState.showLink }));
  }

  sendEmail(model, resetForm, invalidateForm, successCb) {
    this.setState({ to: model.to, cc: model.cc });
    const { subject, to, message } = model;
    const cc = model.cc === '' ? null : model.cc;
    const onCompleted = () => {
      successCb();
    };
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to send email',
        intent: Intent.DANGER
      });
    };

    const mutation = graphql`
      mutation ShareWorkspace_emailWorkspaceLinkMutation($input: EmailWorkspaceLinkInput!) {
        emailWorkspaceLink(input: $input) {
          sent_email_to
          sent_email_cc
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: { workspace_id: this.props.workspace.id, to, cc, subject, message }
      },
      onCompleted,
      onError
    });
  }

  needsEventInfo() {
    const { event } = this.props;
    return event.can_edit && !(event.start_at && event.end_at && event.title);
  }

  updateEvent(model, successCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update event information',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully updated event information',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
    };

    const event = nullifyEmptyStrings(
      DateUtils.omitTimeZoneWithoutTimes(model)
    );

    const mutation = graphql`
      mutation ShareWorkspace_UpdateEventMutation(
        $input: UpdateEventInput!
      ) {
        updateEvent(input: $input) {
          event {
            id
            title
            start_at
            end_at
            time_zone
          }
        }
      }
    `;

    const { title, start_at, end_at, time_zone } = event;
    const optimisticResponse = {
      updateEvent: { event: { id: this.props.event.id, title, start_at, end_at, time_zone } }
    };
    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.event.id,
          title,
          start_at,
          end_at,
          time_zone
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  render() {
    const { event, relay, viewer } = this.props;
    const { needsEventInfo, showLink, to, cc } = this.state;
    const title = 'Share Workspace';
    const documentType = 'workspace';
    const shareButton = (
      <Button
        content='Share'
        basic
        style={{ marginRight: '1em' }}
      />
    );
    const permissionsNode = (
      <div>
        Anyone with the link <strong>can view</strong> this workspace.
      </div>
    );
    const steps = [
      {
        component: ShareOptions,
        componentProps: {
          permissionsNode,
          documentType,
          shareUrl: `${process.env.BASE_URL}/open/w/${this.props.workspace.share_token}`,
          emailSuggestions: this.getUserContactEmails(),
          showLink,
          toggleLink: this.toggleLink,
          canEmail: this.props.workspace.can_email
        },
        title,
        actions: {
          primary: {
            content: showLink ? 'Close' : 'Send Email',
            type: showLink ? 'button' : 'submit'
          },
          secondary: { hide: showLink }
        },
        formProps: {
          onValidSubmit: this.sendEmail,
          autoComplete: 'off'
        }
      },
    ];

    if (!showLink) {
      steps.push({
        component: EmailConfirmation,
        componentProps: { documentType, to, cc, onClose: this.onClose },
        title,
        hideActions: true
      });
    }

    if (needsEventInfo) {
      steps.unshift({
        title,
        component: ConfirmEventDetails,
        componentProps: { relay, viewer, event },
        formProps: {
          onValidSubmit: this.onValidSubmit,
          onInvalidSubmit: this.onInvalidSubmit
        },
        actions: {
          primary: {
            content: 'Next',
            type: 'submit'
          },
          secondary: { hide: !needsEventInfo }
        },
      });
    }
    return (
      <Wizard
        steps={steps}
        trigger={shareButton}
        onOpen={this.onOpen}
        onClose={this.onClose}
      />
    );
  }

}

export default ShareWorkspace;



// WEBPACK FOOTER //
// ./webpack/components/Event/Show/Workspace/Share/index.js